'use client';

import { ReactNode, useEffect, useMemo } from 'react';
import { initializeFirebase } from '@/firebase';
import { FirebaseProvider, type FirebaseContextValue } from './provider';
import { setBearerProvider, scheduleFlush } from '@/lib/competitions/outbox';

interface FirebaseClientProviderProps {
  children: ReactNode;
}

export function FirebaseClientProvider({
  children,
}: FirebaseClientProviderProps) {
  const firebaseContextValue = useMemo<FirebaseContextValue>(() => {
    const { app, auth, firestore } = initializeFirebase();
    return { app, auth, firestore };
  }, []);

  // Wire the offline mutation outbox to the Firebase auth so any
  // queued Bearer-mode write resolves a fresh ID token at flush
  // time. Tokens expire after an hour; the outbox never stores
  // them — it only asks the provider for one when it needs one.
  useEffect(() => {
    const auth = firebaseContextValue.auth;
    setBearerProvider(async () => {
      try {
        return (await auth.currentUser?.getIdToken()) ?? null;
      } catch {
        return null;
      }
    });
    // Kick a flush in case the page was loaded with pending
    // writes from a previous session that crashed mid-sync.
    scheduleFlush();
  }, [firebaseContextValue]);

  return (
    <FirebaseProvider value={firebaseContextValue}>{children}</FirebaseProvider>
  );
}
